import { Component } from '@theme/component';

/**
 * @typedef {Object} AskButtonRefs
 * @property {HTMLButtonElement} button - The trigger button
 */

/**
 * Opens the Okendo "Ask a Question" form in a new tab.
 *
 * Resolves the destination URL at click time from Okendo's own on-page
 * markup so it always matches the store's live subscriber id and product,
 * independent of which Okendo widget tab is currently active.
 *
 * @extends {Component<AskButtonRefs>}
 */
class NcnQaAskButton extends Component {
  requiredRefs = ['button'];

  open() {
    const url = this.#resolveUrl();
    if (url) window.open(url, '_blank', 'noopener');
  }

  /**
   * @returns {string} The Okendo question form URL.
   */
  #resolveUrl() {
    // Prefer Okendo's native ask-a-question link when it is in the DOM.
    const native = document.querySelector('a.oke-qw-askQuestion');
    if (native instanceof HTMLAnchorElement && native.href) return native.href;

    // Otherwise borrow the subscriber id from any Okendo link on the page
    // (e.g. the always-present "Write a review" button) and build the URL.
    const subscriberId = this.#subscriberId();
    const productId = this.dataset.productId;
    if (!subscriberId || !productId) return '';

    const locale = this.dataset.locale || 'en';
    return `https://okendo.reviews/question?subscriberId=${encodeURIComponent(subscriberId)}&productId=${encodeURIComponent(productId)}&locale=${encodeURIComponent(locale)}&okeChannel=questions-widget-ask-question-button`;
  }

  /**
   * @returns {string} The Okendo subscriber id discovered on the page.
   */
  #subscriberId() {
    for (const link of document.querySelectorAll('a[href*="okendo.reviews"]')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      try {
        const id = new URL(link.href).searchParams.get('subscriberId');
        if (id) return id;
      } catch {
        // Ignore malformed URLs.
      }
    }
    return '';
  }
}

if (!customElements.get('ncn-qa-ask-button')) {
  customElements.define('ncn-qa-ask-button', NcnQaAskButton);
}
